import React, { useState, useEffect } from 'react';
import { FormInstance } from 'antd/es/form';
import TextArea from 'antd/lib/input/TextArea';
import produce from 'immer';
import InterviewQuestionsInput from 'components/community/parts/InterviewQuestionsInput';
import InterviewTypesProportionInput from 'components/community/parts/InterviewTypesProportionInput';
import { TInterviewContent } from 'modules/community';
import {
  CInterviewProgress,
  CInterviewAtmosphere,
  CInterviewDificultyLevel,
} from 'constants/community.const';
import { EInterviewProgress, EInterviewAtmosphere, EInterviewDificultyLevel } from 'enums/community.enum';
import { Form, Radio, Input, Checkbox, Divider } from 'antd';
import styled from 'styled-components';

type TSharingInterviewFormProps = {
  isConsultingFirm: boolean;
  form?: FormInstance;
  checkedRound: Array<number>;
  boardName: string;
  totalRound: number;
  isFinal?: boolean;
};

const SharingInterviewForm = ({
  isConsultingFirm,
  form,
  checkedRound,
  boardName,
  totalRound,
  isFinal,
}: TSharingInterviewFormProps) => {
  const [activateInterviewRound, setActivateInterviewRound] = useState<Array<number>>();

  const onRoundListChange = () => {
    if (form) {
      const checkedList = form
        .getFieldValue('interview_review')
        .map((el: TInterviewContent) =>
          //el.interview_type?.length>0 이면 지금 해당 체크박스가 체크되어 있다는것
          el.interview_type?.length > 0 ? el.interview_type[0] : undefined,
        )
        .filter((el: number | undefined) => el); //undefined 필터링
      setActivateInterviewRound(checkedList);
    }
  };

  useEffect(() => {
    // 입력폼 기본 세팅 (Form.List 는 초기에 세팅해줘야 렌더링된다)
    form &&
      form.setFieldsValue({
        interview_review: Array(totalRound).fill({ interview_question: [undefined, undefined] }),
      });
  }, []);

  useEffect(() => {
    setActivateInterviewRound(checkedRound);
  }, [checkedRound]);

  //컨설팅 회사 여부 값이 바뀌었을 때 true -> false로 바뀔 경우 fit/case 비중 부분 모두 초기화 시켜주는 로직
  useEffect(() => {
    if (!isConsultingFirm) {
      if (form) {
        //   const { interview_review } = form.getFieldsValue(); //Form.List value 값에 접근 => 배열 형태
        //   interview_review.forEach((el: any, index: number) =>
        //     Object.assign(interview_review[index], { fit_importance: undefined, case_importance: undefined }),
        //   ); // interview_review 의 모든 인덱스에 접근해서 출처 객체(두번째 인자)들의 속성을 복사해 대상 객체(첫번째 인자)에 붙여넣음
        //   form.setFieldsValue({ interview_review }); //interview_review 는 대상 객체가 붙여넣어진 출처 객체로 바뀐 형태가 되며, 이 값을 setFieldsValue
        // }

        //Object.assign은 ES6에서 추가된 메소드이고 Internet Explorer는 지원하지 않으며
        //Object.assign를사용하여 불변 객체를 만드는 방법은 성능상 이슈가 있을 수 있어 immer를 사용한 방법으로 변경
        const { interview_review } = form.getFieldsValue(); //Form.List value 값에 접근 => 배열 형태
        const nextState = produce(interview_review, (draft: any) => {
          draft.forEach((el: any, index: number) => {
            el.fit_importance = undefined;
            el.case_importance = undefined;
          });
        });

        form.setFieldsValue({ interview_review: nextState });
      }
    }
  }, [isConsultingFirm]);

  return (
    <SharingInterviewFormWrapper>
      <Input.Group>
        <Form.List name='interview_review'>
          {(fields, { add, remove }) => {
            return (
              <div>
                {fields.map((field, index) => {
                  return (
                    <div key={field.key}>
                      {/*  activateInterviewRound에 있는 차수만 보일 수 있도록 */}
                      {activateInterviewRound?.includes(field.name + 1) && (
                        <div>
                          <div
                            className='interview_round'
                            // 전 차수 체크박스 클릭 불가능하도록
                            style={{ pointerEvents: 'none' }}
                          >
                            <Form.Item
                              name={[field.name, `interview_type`]}
                              initialValue={
                                activateInterviewRound?.includes(field.name + 1)
                                  ? [field.name + 1]
                                  : undefined
                              }
                            >
                              <Checkbox.Group
                                onChange={() => {
                                  onRoundListChange();
                                }}
                              >
                                <Checkbox className='green-border-checkbox' value={field.name + 1}>
                                  {index + 1}차 인터뷰{' '}
                                </Checkbox>
                              </Checkbox.Group>
                            </Form.Item>
                          </div>
                          <div>
                            <div>인터뷰 방법</div>
                            <Form.Item
                              className='form-item'
                              name={[field.name, `interview_progress`]}
                              rules={[{ required: true }]}
                            >
                              <Radio.Group className={`col-8`}>
                                <Radio.Button value={EInterviewProgress.FaceToFace}>
                                  {CInterviewProgress[EInterviewProgress.FaceToFace]}
                                </Radio.Button>
                                <Radio.Button value={EInterviewProgress.Zoom}>
                                  {CInterviewProgress[EInterviewProgress.Zoom]}
                                </Radio.Button>
                                <Radio.Button value={EInterviewProgress.Call}>
                                  {CInterviewProgress[EInterviewProgress.Call]}
                                </Radio.Button>
                              </Radio.Group>
                            </Form.Item>
                            <div>면접장 및 면접관 분위기</div>
                            <Form.Item
                              className='form-item'
                              name={[field.name, `interview_atmosphere`]}
                              rules={[{ required: true }]}
                            >
                              <Radio.Group className={`col-8`}>
                                <Radio.Button
                                  className={`green-border`}
                                  value={EInterviewAtmosphere.Comfortable}
                                >
                                  {CInterviewAtmosphere[EInterviewAtmosphere.Comfortable]}
                                </Radio.Button>
                                <Radio.Button className={`navy-border`} value={EInterviewAtmosphere.Normal}>
                                  {CInterviewAtmosphere[EInterviewAtmosphere.Normal]}
                                </Radio.Button>
                                <Radio.Button
                                  className={`red-border`}
                                  value={EInterviewAtmosphere.Uncomfortable}
                                >
                                  {CInterviewAtmosphere[EInterviewAtmosphere.Uncomfortable]}
                                </Radio.Button>
                              </Radio.Group>
                            </Form.Item>
                            <div>인터뷰 난이도</div>
                            <Form.Item
                              className='form-item'
                              name={[field.name, `interview_level`]}
                              rules={[{ required: true }]}
                            >
                              <Radio.Group className={`col-8`}>
                                <Radio.Button
                                  className={`green-border`}
                                  value={EInterviewDificultyLevel.Easy}
                                >
                                  {CInterviewDificultyLevel[EInterviewDificultyLevel.Easy]}
                                </Radio.Button>
                                <Radio.Button
                                  className={`navy-border`}
                                  value={EInterviewDificultyLevel.Normal}
                                >
                                  {CInterviewDificultyLevel[EInterviewDificultyLevel.Normal]}
                                </Radio.Button>
                                <Radio.Button
                                  className={`red-border`}
                                  value={EInterviewDificultyLevel.Difficulty}
                                >
                                  {CInterviewDificultyLevel[EInterviewDificultyLevel.Difficulty]}
                                </Radio.Button>
                              </Radio.Group>
                            </Form.Item>
                            {isConsultingFirm && (
                              <>
                                <div>인터뷰 시간 중 Fit 유형 비중</div>
                                <InterviewTypesProportionInput fieldName={field.name} form={form} />
                              </>
                            )}
                            <div>기억에 남는 인터뷰 질문 두가지 (Fit or Case)</div>
                            <InterviewQuestionsInput fieldName={field.name} />
                            {isFinal && (
                              <>
                                <div>
                                  문제 풀이 접근법 (Case 문제 미출제 시 Fit 질문에서 본인만의 면접관 설득
                                  노하우를 알려주세요)
                                </div>
                                <Form.Item
                                  className='form-item'
                                  name={[field.name, 'problem_solving']}
                                  rules={[
                                    { required: true },
                                    { min: 150, message: '최소 150자 이상이어야 합니다' },
                                  ]}
                                  // rules={[{ required: true }, { type: 'string', min: 150 }]}
                                >
                                  <TextArea
                                    placeholder='크게 다섯 가지 순서로 문제를 풀었습니다. 흩어진 금융그룹 계열사들에게 현재 어떤 문제가 발생하는지 정의, 이전 시 이 문제가 해결되는지 판단했습니다. 문제가 해결된다면 이전에 드는 비용 대비 효과가 얼마나 큰지 파악 후 비용 대비 효과가 큰 다른 옵션은 없는지 비교했습니다. 마지막으로 기타 리스크 및 시너지 등 요소 파악을 진행했습니다. 이 다섯 가지 각각에 대해 소결을 내려서 이전 or 이전하지 않음에 대해 결론을 내겠다고 접근했습니다.'
                                    autoSize={{ minRows: 4 }}
                                    size='large'
                                  />
                                </Form.Item>
                              </>
                            )}
                            <Divider style={{ borderTop: `2px solid #E6FBE6` }} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          }}
        </Form.List>
      </Input.Group>
    </SharingInterviewFormWrapper>
  );
};

export default SharingInterviewForm;

const SharingInterviewFormWrapper = styled.div`
  word-break: keep-all;

  & .interview_round .ant-form-item {
    margin-bottom: 0;
  }
`;
